import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { Bitcoin, TrendingUp, Cpu, Zap, Home, Download, Link2, Check, BarChart2, X, ChevronLeft, ChevronRight } from 'lucide-react';

const BASE_URL = 'https://utxomacro.com';
const getChartCanonical = (chartId: string) => `${BASE_URL}/charts/${chartId}`;

// ── X logo SVG ────────────────────────────────────────────────────────────────
const XLogo: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// ── Types ─────────────────────────────────────────────────────────────────────
interface ChartInfo {
  what:   string;
  why:    string;
  signal: string | null;
}

interface ChartEntry {
  id: string;
  date: string;
  type: string;
  category: string;
  categoryColor: string;
  title: string;
  subtitle: string;
  annotations: Array<string | { text: string; positive: boolean }>;
  source: string;
  note: string;
  info?: ChartInfo;
  pngFile: string;
  publishedAt: string;
}

// ── Category config ───────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'all',     label: 'All',     color: '#F7931A', icon: <BarChart2  size={13} /> },
  { id: 'bitcoin', label: 'Bitcoin', color: '#F7931A', icon: <Bitcoin    size={13} /> },
  { id: 'macro',   label: 'Macro',   color: '#3B82F6', icon: <TrendingUp size={13} /> },
  { id: 'energy',  label: 'Energy',  color: '#F59E0B', icon: <Zap        size={13} /> },
  { id: 'housing', label: 'Housing', color: '#8B5CF6', icon: <Home       size={13} /> },
  { id: 'ai',      label: 'AI',      color: '#10B981', icon: <Cpu        size={13} /> },
];

function getCategoryConfig(category: string) {
  return CATEGORIES.find(c => c.id === category.toLowerCase()) ?? CATEGORIES[0];
}

// ── Annotation pills ──────────────────────────────────────────────────────────
const AnnotationPills: React.FC<{ annotations: ChartEntry['annotations'] }> = ({ annotations }) => (
  <div className="flex flex-wrap gap-1.5">
    {annotations.map((a, i) => {
      const text     = typeof a === 'string' ? a : a.text;
      const positive = typeof a === 'string' ? true : (a as any).positive !== false;
      return (
        <span
          key={i}
          className="text-xs font-semibold px-2 py-0.5 rounded border"
          style={{
            color:       positive ? '#F7931A' : '#FF3B3B',
            background:  positive ? 'rgba(247,147,26,0.08)' : 'rgba(255,59,59,0.10)',
            borderColor: positive ? 'rgba(247,147,26,0.22)' : 'rgba(255,59,59,0.35)',
          }}
        >
          {text}
        </span>
      );
    })}
  </div>
);

// ── Info Popover ─────────────────────────────────────────────────────────────
const InfoButton: React.FC<{ info: ChartInfo; color: string }> = ({ info, color }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        title="What is this metric?"
        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold italic transition-all border ${
          open ? 'bg-white/10' : 'border-white/15 text-gray-500 hover:text-gray-200 hover:border-white/30'
        }`}
        style={open ? { borderColor: color, color, border: `1px solid ${color}` } : {}}
      >
        i
      </button>
      {open && (
        <div
          className="absolute right-0 top-8 z-30 w-72 rounded-xl border border-white/10 bg-[#141414] shadow-2xl p-4 space-y-3"
          style={{ boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px ${color}20` }}
        >
          <div className="absolute -top-1.5 right-3 w-3 h-3 rotate-45 bg-[#141414] border-l border-t border-white/10" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color }}>What is this?</p>
            <p className="text-xs text-gray-400 leading-relaxed">{info.what}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Why it matters</p>
            <p className="text-xs text-gray-400 leading-relaxed">{info.why}</p>
          </div>
          {info.signal && (
            <div className="rounded-lg px-3 py-2 border" style={{ background: `${color}08`, borderColor: `${color}25` }}>
              <p className="text-xs font-semibold" style={{ color }}>📊 {info.signal}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Download helper ───────────────────────────────────────────────────────────
const handleDownload = async (chart: ChartEntry) => {
  try {
    const res  = await fetch(chart.pngFile);
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `utxomacro-${chart.id}.png`; a.click();
    URL.revokeObjectURL(url);
  } catch { window.open(chart.pngFile, '_blank'); }
};

// ── Action buttons ────────────────────────────────────────────────────────────
const ActionBar: React.FC<{ chart: ChartEntry; dark?: boolean }> = ({ chart, dark = false }) => {
  const [copied, setCopied] = useState(false);
  const base = dark
    ? 'border-white/20 text-gray-300 hover:text-white hover:border-white/40 bg-white/5'
    : 'border-white/10 text-gray-400 hover:text-white hover:border-white/25';

  // Always share canonical /charts/:id URL — not window.location which may
  // still read /charts when the lightbox opened without a navigation event.
  const canonicalUrl = getChartCanonical(chart.id);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={() => handleDownload(chart)}
        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${base}`}
      >
        <Download size={12} /> Download
      </button>
      <a
        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${chart.title} — via @UTXOMacro`)}&url=${encodeURIComponent(canonicalUrl)}`}
        target="_blank" rel="noopener noreferrer"
        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${base}`}
      >
        <XLogo size={12} /> Share on X
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`}
        target="_blank" rel="noopener noreferrer"
        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${base}`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        LinkedIn
      </a>
      <button
        onClick={() => { navigator.clipboard.writeText(canonicalUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${base}`}
      >
        {copied ? <Check size={12} className="text-green-400" /> : <Link2 size={12} />}
        {copied ? 'Copied!' : 'Copy link'}
      </button>
    </div>
  );
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox: React.FC<{
  chart: ChartEntry;
  charts: ChartEntry[];
  onClose: () => void;
}> = ({ chart, charts, onClose }) => {
  const [current, setCurrent] = useState(chart);
  const idx      = charts.findIndex(c => c.id === current.id);
  const hasPrev  = idx > 0;
  const hasNext  = idx < charts.length - 1;
  const cat      = getCategoryConfig(current.category);

  const prev = useCallback(() => { if (hasPrev) setCurrent(charts[idx - 1]); }, [hasPrev, charts, idx]);
  const next = useCallback(() => { if (hasNext) setCurrent(charts[idx + 1]); }, [hasNext, charts, idx]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowLeft')   prev();
      if (e.key === 'ArrowRight')  next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      {/* Modal */}
      <div
        className="relative w-full max-w-5xl bg-[#0F0F0F] border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 border border-white/10 text-gray-400 hover:text-white transition-colors"
        >
          <X size={15} />
        </button>

        {/* Prev / Next */}
        {hasPrev && (
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/70 border border-white/10 text-gray-300 hover:text-white transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        {hasNext && (
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/70 border border-white/10 text-gray-300 hover:text-white transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        )}

        {/* Chart image — large */}
        <div className="w-full bg-[#0A0A0A] border-b border-white/5 flex-shrink-0" style={{ maxHeight: '58vh' }}>
          <img
            src={current.pngFile}
            alt={current.title}
            className="w-full h-full object-contain"
            style={{ maxHeight: '58vh' }}
          />
        </div>

        {/* Meta */}
        <div className="px-6 py-5 overflow-y-auto">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wider"
              style={{ background: `${cat.color}15`, color: cat.color, border: `1px solid ${cat.color}28` }}
            >
              {cat.icon} {current.category}
            </span>
            <span className="text-xs text-gray-600">{current.date}</span>
            <span className="text-xs text-gray-700">{idx + 1} / {charts.length}</span>
          </div>

          <div className="flex items-start gap-2 mb-1">
            <h2 className="text-white font-bold text-lg leading-snug flex-1">{current.title}</h2>
            {current.info && <InfoButton info={current.info} color={cat.color} />}
          </div>
          <p className="text-gray-500 text-sm mb-4">{current.subtitle}</p>

          {current.annotations?.length > 0 && (
            <div className="mb-4"><AnnotationPills annotations={current.annotations} /></div>
          )}

          <p className="text-gray-700 text-xs mb-4">Source: {current.source}</p>

          <div className="pt-4 border-t border-white/5">
            <ActionBar chart={current} dark />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Hero ──────────────────────────────────────────────────────────────────────
const ChartHero: React.FC<{ chart: ChartEntry; onClick: () => void }> = ({ chart, onClick }) => {
  const cat = getCategoryConfig(chart.category);
  return (
    <div
      className="relative mb-14 rounded-2xl overflow-hidden border border-white/5 cursor-pointer group"
      style={{ background: '#0D0D0D', boxShadow: '0 0 60px rgba(247,147,26,0.04)' }}
      onClick={onClick}
    >
      <div className="grid grid-cols-1 lg:grid-cols-5">
        {/* Image — takes 3/5 on desktop */}
        <div className="lg:col-span-3 relative bg-[#0A0A0A] border-b lg:border-b-0 lg:border-r border-white/5 overflow-hidden"
          style={{ minHeight: '320px' }}>
          <img
            src={chart.pngFile}
            alt={chart.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            style={{ minHeight: '320px' }}
          />
          {/* Gradient overlay on hover hint */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60 border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
              Click to expand
            </span>
          </div>
          {/* Latest badge */}
          <div className="absolute top-4 left-4">
            <span className="text-xs font-black px-2.5 py-1 rounded bg-[#F7931A] text-black uppercase tracking-wider shadow-lg">
              Latest
            </span>
          </div>
        </div>

        {/* Meta — takes 2/5 */}
        <div className="lg:col-span-2 p-7 flex flex-col justify-between" onClick={e => e.stopPropagation()}>
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span
                className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wider"
                style={{ background: `${cat.color}18`, color: cat.color, border: `1px solid ${cat.color}30` }}
              >
                {cat.icon} {chart.category}
              </span>
              <span className="text-xs text-gray-600">{chart.date}</span>
            </div>

            <div className="flex items-start gap-2 mb-2">
              <h2 className="text-white font-bold text-xl sm:text-2xl leading-snug tracking-tight flex-1">{chart.title}</h2>
              {chart.info && <InfoButton info={chart.info} color={cat.color} />}
            </div>
            <p className="text-gray-500 text-sm mb-5 leading-relaxed">{chart.subtitle}</p>

            {chart.annotations?.length > 0 && (
              <div className="mb-5"><AnnotationPills annotations={chart.annotations} /></div>
            )}

            <p className="text-gray-700 text-xs mb-6">Source: {chart.source}</p>
          </div>

          <div className="space-y-3">
            <ActionBar chart={chart} />
            <button
              onClick={onClick}
              className="w-full text-xs font-semibold py-2 rounded-lg border border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/8 transition-all"
            >
              View full size ↗
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Latest by Category strip ──────────────────────────────────────────────────
const LatestStrip: React.FC<{ charts: ChartEntry[]; onOpen: (c: ChartEntry) => void }> = ({ charts, onOpen }) => {
  const seen    = new Set<string>();
  const latest: ChartEntry[] = [];
  for (const c of charts) {
    const key = c.category.toLowerCase();
    if (!seen.has(key)) { seen.add(key); latest.push(c); }
    if (latest.length >= 5) break;
  }
  if (latest.length < 2) return null;

  return (
    <div className="mb-12">
      <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Latest by Category</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {latest.map(chart => {
          const cat = getCategoryConfig(chart.category);
          return (
            <div
              key={chart.id}
              onClick={() => onOpen(chart)}
              className="border border-white/5 rounded-xl overflow-hidden bg-[#111111] cursor-pointer group transition-all hover:border-white/20"
              style={{ boxShadow: '0 0 0 1px transparent' }}
              onMouseEnter={e => (e.currentTarget.style.boxShadow = `0 0 20px 0 ${cat.color}12`)}
              onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 0 0 1px transparent')}
            >
              <div className="relative w-full bg-[#0A0A0A] overflow-hidden" style={{ aspectRatio: '16/9' }}>
                <img
                  src={chart.pngFile}
                  alt={chart.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
              </div>
              <div className="p-2.5">
                <span
                  className="inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded uppercase tracking-wider mb-1.5"
                  style={{ background: `${cat.color}15`, color: cat.color }}
                >
                  {cat.icon} {chart.category}
                </span>
                <p className="text-white text-xs font-semibold leading-snug line-clamp-2">{chart.title}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Chart Card (grid) ─────────────────────────────────────────────────────────
const ChartCard: React.FC<{ chart: ChartEntry; onOpen: (c: ChartEntry) => void }> = ({ chart, onOpen }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const cat = getCategoryConfig(chart.category);

  return (
    <div
      onClick={() => onOpen(chart)}
      className="group border border-white/5 rounded-2xl overflow-hidden transition-all bg-[#111111] cursor-pointer"
      onMouseEnter={e => (e.currentTarget.style.boxShadow = `0 0 28px 0 ${cat.color}12, 0 0 0 1px ${cat.color}18`)}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = '')}
    >
      {/* Image */}
      <div className="relative w-full bg-[#0A0A0A] border-b border-white/5 overflow-hidden" style={{ aspectRatio: '16/9' }}>
        {!imgLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <BarChart2 size={24} className="text-gray-700 animate-pulse" />
          </div>
        )}
        <img
          src={chart.pngFile}
          alt={chart.title}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImgLoaded(true)}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-300 flex items-end justify-end p-3">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs text-white/70 bg-black/60 px-2 py-1 rounded-md">
            Expand ↗
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wider"
            style={{ background: `${cat.color}15`, color: cat.color, border: `1px solid ${cat.color}28` }}
          >
            {cat.icon} {chart.category}
          </span>
          <span className="text-xs text-gray-600">{chart.date}</span>
        </div>

        <div className="flex items-start gap-2 mb-1">
          <h3 className="text-white font-bold text-base leading-snug flex-1">{chart.title}</h3>
          {chart.info && <InfoButton info={chart.info} color={cat.color} />}
        </div>
        <p className="text-gray-500 text-xs mb-4">{chart.subtitle}</p>

        {chart.annotations?.length > 0 && (
          <div className="mb-4"><AnnotationPills annotations={chart.annotations} /></div>
        )}

        <p className="text-gray-700 text-xs mb-4">Source: {chart.source}</p>

        <div className="pt-3 border-t border-white/5" onClick={e => e.stopPropagation()}>
          <ActionBar chart={chart} />
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
interface DailyChartsProps { onNavigate: (page: string) => void; }

const DailyCharts: React.FC<DailyChartsProps> = ({ onNavigate }) => {
  const { id: routeChartId } = useParams<{ id: string }>();
  const navigateRouter = useNavigate();

  const [charts, setCharts]               = useState<ChartEntry[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightbox, setLightbox]           = useState<ChartEntry | null>(null);
  const [chartsExpanded, setChartsExpanded] = useState(false);

  // Dynamic SEO: update OG/Twitter meta per-chart when lightbox is open
  // so sharing a /charts/:id URL shows the correct chart image + title.
  useSEO(lightbox ? {
    title: lightbox.title,
    description: `${lightbox.subtitle} — Source: ${lightbox.source}`,
    image: lightbox.pngFile.startsWith('http')
      ? lightbox.pngFile
      : `${BASE_URL}${lightbox.pngFile.startsWith('/') ? '' : '/'}${lightbox.pngFile}`,
    url: getChartCanonical(lightbox.id),
    type: 'article',
    category: lightbox.category,
  } : {
    title: 'Daily Charts',
    description: 'Daily Bitcoin, macro, and economic charts from UTXOMacro. CPI, yield curves, on-chain metrics, housing, and more.',
    url: 'https://utxomacro.com/charts',
    keywords: 'Bitcoin charts, CPI chart, yield curve, on-chain metrics, macro charts, UTXOMacro',
  });
  const CHARTS_LIMIT = 6;

  // Open lightbox: update URL to /charts/:id for shareable deep links
  const openLightbox = useCallback((chart: ChartEntry) => {
    setLightbox(chart);
    navigateRouter(`/charts/${chart.id}`, { replace: false });
  }, [navigateRouter]);

  // Close lightbox: return URL to /charts
  const closeLightbox = useCallback(() => {
    setLightbox(null);
    navigateRouter('/charts', { replace: false });
  }, [navigateRouter]);

  useEffect(() => {
    fetch('/data/charts.json')
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: ChartEntry[]) => {
        const sorted = [...data].sort((a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
        );
        setCharts(sorted);
        setLoading(false);
        // Auto-open chart if navigated directly to /charts/:id
        if (routeChartId) {
          const target = sorted.find(c => c.id === routeChartId);
          if (target) setLightbox(target);
        }
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [routeChartId]);

  const presentCategories = ['all', ...Array.from(new Set(charts.map(c => c.category.toLowerCase())))];
  const visibleCategories = CATEGORIES.filter(c => presentCategories.includes(c.id));

  const filtered = activeCategory === 'all'
    ? charts
    : charts.filter(c => c.category.toLowerCase() === activeCategory);

  // Hero = most recent chart overall
  const heroChart = charts[0] ?? null;

  // Grid = all except hero when showing all
  const gridCharts = activeCategory === 'all' ? filtered.slice(1) : filtered;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      {lightbox && (
        <Lightbox
          chart={lightbox}
          charts={filtered.length > 0 ? filtered : charts}
          onClose={closeLightbox}
        />
      )}

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">

        {/* Page header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-white mb-1">Daily Charts</h1>
          <div className="w-12 h-0.5 bg-[#F7931A] rounded mb-4" />
          <p className="text-gray-500 text-sm max-w-xl">
            One chart per day. Bitcoin, macro, energy, housing — signal over noise. Click any chart to expand.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24">
            <BarChart2 size={28} className="text-[#F7931A] animate-pulse" />
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center justify-center py-24 text-red-500/60 text-sm">
            Failed to load charts: {error}
          </div>
        )}

        {!loading && !error && charts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-600">
            <BarChart2 size={36} className="opacity-30" />
            <p className="text-sm">No charts published yet. Check back after the next pipeline run.</p>
          </div>
        )}

        {!loading && !error && charts.length > 0 && (
          <>
            {/* Hero — most recent chart, full width */}
            {heroChart && activeCategory === 'all' && (
              <ChartHero chart={heroChart} onClick={() => openLightbox(heroChart!)} />
            )}

            {/* Latest by Category */}
            {activeCategory === 'all' && (
              <LatestStrip charts={charts} onOpen={openLightbox} />
            )}

            {/* Category filter */}
            <div className="flex items-center gap-2 flex-wrap mb-8">
              {visibleCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.id); setChartsExpanded(false); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    activeCategory === cat.id
                      ? 'text-white border-transparent'
                      : 'border-white/8 text-gray-500 hover:border-white/20 hover:text-gray-300'
                  }`}
                  style={activeCategory === cat.id
                    ? { background: `${cat.color}18`, borderColor: `${cat.color}40`, color: cat.color }
                    : {}
                  }
                >
                  {cat.icon} {cat.label}
                  <span className="text-xs opacity-60">
                    {cat.id === 'all' ? charts.length : charts.filter(c => c.category.toLowerCase() === cat.id).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Section label */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-white mb-1">
                {activeCategory === 'all' ? 'All Charts' : CATEGORIES.find(c => c.id === activeCategory)?.label}
              </h2>
              <div className="w-8 h-0.5 rounded" style={{ background: getCategoryConfig(activeCategory).color }} />
            </div>

            {/* Grid */}
            {gridCharts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(chartsExpanded ? gridCharts : gridCharts.slice(0, CHARTS_LIMIT)).map(chart => (
                    <ChartCard key={chart.id} chart={chart} onOpen={openLightbox} />
                  ))}
                </div>
                {gridCharts.length > CHARTS_LIMIT && (
                  <button
                    onClick={() => setChartsExpanded(prev => !prev)}
                    className="mt-8 w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white text-sm font-medium transition-all"
                  >
                    {chartsExpanded ? (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>
                        Show less
                      </>
                    ) : (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                        Show {gridCharts.length - CHARTS_LIMIT} more charts
                      </>
                    )}
                  </button>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-16 text-gray-600 text-sm">
                No {activeCategory} charts yet.
              </div>
            )}
          </>
        )}
      </main>

      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default DailyCharts;
